import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as echarts from 'echarts';
import { Subscription, of } from 'rxjs';
import { DialogComponent } from '../../shared/component/dynamic-form/dialog-box/dialog-component';
import { Field } from '../../shared/models/field.model';
import { ProductRepository } from '../../shared/repositories/products.repository';
import { ProductsService } from '../../shared/services/products.service';
import { SharedStateService } from '../../shared/services/shared-state.service';
import { ChartFullscreenDialogComponent } from './chart-fullscreen-dialog/chart-fullscreen-dialog.component';

@Component({
  selector: 'app-graph',
  templateUrl: './graph.component.html',
  styleUrls: ['./graph.component.scss'],
  standalone: false,
})
export class GraphComponent implements AfterViewInit, OnInit, OnDestroy {
  formValues: any = {};
  selectedModel: string = '';
  allRequiredFieldsPresent: boolean = false;
  heatRecoveryUsed: boolean = true;
  private latestFormState: any;
  // dynamic form fields and models for the form embedded in the Graph tab
  formFields: Field[] = [];
  models: string[] = [];
  outerkeys: number[] = [];
  // Whether we have fetched backend data to render graphs
  hasData: boolean = false;

  private readonly commonColor = '#007bc3'; // All graph lines will have this color

  constructor(
    private productRepository: ProductRepository,
    private productsService: ProductsService,
    private sharedStateService: SharedStateService,
    private dialog: MatDialog,
  ) {}

  onFormValueChanged(event: { formValues: any; requiredFields: any; selectedUnits: any }): void {
    if (!event) {
      return;
    }

    this.latestFormState = event;
    this.formValues = event.formValues || {};

    // keep selectedModel in sync if provided
    if (this.formValues && this.formValues['selectedModel']) {
      this.selectedModel = this.formValues['selectedModel'];
    }

    const requiredFields = event.requiredFields;
    if (!requiredFields || requiredFields.length === 0) {
      // No fields declared required by the form - allow submit
      this.allRequiredFieldsPresent = true;
      setTimeout(() => this.updateCharts(), 0);
      return;
    }

    const visibleRequiredFields = this.filterVisibleRequiredFields(requiredFields);

    const allFieldsFilled = visibleRequiredFields.every((field: any) => this.isRequiredFieldSatisfied(field));

    const modelSelected = this.isModelSelected();

    this.allRequiredFieldsPresent = allFieldsFilled && modelSelected;

    // Re-render charts to reflect any changes immediately
    setTimeout(() => this.updateCharts(), 0);
  }

  private isHeatRecoveryField(fieldName: string): boolean {
    return (
      fieldName === 'temperatureAfterRecovery' ||
      fieldName === 'relativeHumidityAfterRecovery' ||
      fieldName === 'absolutePressureAfterRecovery'
    );
  }

  private filterVisibleRequiredFields(requiredFields: any[]): any[] {
    return requiredFields.filter((field: any) => {
      if (this.isHeatRecoveryField(field.name)) {
        return this.heatRecoveryUsed;
      }
      return true;
    });
  }

  private isRequiredFieldSatisfied(field: any): boolean {
    const raw = this.formValues ? this.formValues[field.name] : undefined;
    if (raw === undefined || raw === null || raw === '') {
      return false;
    }

    const maybeNum = typeof raw === 'number' ? raw : Number(raw);
    const isNumber = !Number.isNaN(maybeNum);

    if ((field.min !== undefined || field.max !== undefined) && isNumber) {
      if (field.min !== undefined && maybeNum < field.min) {
        return false;
      }
      if (field.max !== undefined && maybeNum > field.max) {
        return false;
      }
    }

    return true;
  }

  private isModelSelected(): boolean {
    return !!(
      this.formValues &&
      this.formValues['selectedModel'] !== undefined &&
      this.formValues['selectedModel'] !== null &&
      this.formValues['selectedModel'] !== ''
    );
  }

  onModelSelected(model: string): void {
    this.selectedModel = model;
    // Re-render charts in case operating point or markers depend on model selection
    setTimeout(() => this.updateCharts(), 0);
  }

  onGraphSubmit(): void {
    // formValues is expected to be the converted/base-unit values emitted by the dynamic form
    if (!this.formValues || Object.keys(this.formValues).length === 0) {
      this.dialog.open(DialogComponent, {
        data: { message: 'Form values are not defined' },
      });
      return;
    }

    // Update sensor values in the service so other components can react
    try {
      this.productsService.updateSensorValues({ sensorValues: this.formValues });
    } catch (e) {
      // swallow silently; updateSensorValues is local and shouldn't throw
    }

    // If a specific model is selected, fetch that model from backend and update service caches
    if (this.selectedModel && this.selectedModel !== '') {
      this.productsService.getSpecificModel(this.formValues, this.selectedModel).subscribe((res) => {
        if (res && Array.isArray(res) && res.length > 0) {
          this.productsService.updateFetchedData(res);
          this.productsService.updateModelData(res);
          // update charts from the new data
          this.updateChartWithCachedData(res[0]);
        }
      });
    } else {
      // No specific model chosen - broadcast submit event
      this.sharedStateService.triggerSubmitClicked();
      // update charts (client-side) with current values
      this.updateCharts();
    }
  }

  ngOnInit(): void {
    // Initialize reactive subscriptions via small helper methods to keep this method concise
    this.subscribeToFormFields();
    this.subscribeToModelNames();
    this.subscribeToFetchedData();
    this.subscribeToEfficiencyToggles();
    this.subscribeToHeatRecovery();
  }

  private subscribeToFormFields(): void {
    const fieldsObs = this.productsService?.formFields$ ?? of([]);
    const fieldsSub = fieldsObs.subscribe((fields: Field[] | null) => {
      this.formFields = fields || [];
    });
    this.subscriptions.push(fieldsSub);
  }

  private subscribeToModelNames(): void {
    const modelsObs =
      this.productsService?.modelNames$ ?? (this.productsService?.getModelNames ? this.productsService.getModelNames() : of([]));
    const modelsSub = modelsObs.subscribe((data: string[] | null) => {
      this.models = data || [];
    });
    this.subscriptions.push(modelsSub);
  }

  private subscribeToFetchedData(): void {
    const fetchedObs = this.productsService?.fetchedData$ ?? of(null);
    const fetchedSub = fetchedObs.subscribe((data: any) => {
      if (data && Array.isArray(data) && data.length > 0) {
        this.hasData = true;
        this.updateChartWithCachedData(data[0]);
      } else {
        // No data available yet - clear charts and show prompt
        this.hasData = false;
        this.seriesMap = {};
        this.markerYMap = {};
        setTimeout(() => this.updateCharts(), 0);
      }
    });
    this.subscriptions.push(fetchedSub);
  }

  private subscribeToEfficiencyToggles(): void {
    const adiabaticObs = this.sharedStateService?.adiabaticEfficiency$ ?? of(false);
    const totalObs = this.sharedStateService?.totalEfficiency$ ?? of(false);
    this.subscriptions.push(
      adiabaticObs.subscribe((val: boolean) => {
        this.adiabaticEnabled = val;
        const prev = this.showEfficiencyChart;
        this.showEfficiencyChart = this.adiabaticEnabled || this.totalEnabled;
        if (!prev && this.showEfficiencyChart) {
          setTimeout(() => this.updateCharts(), 0);
        } else {
          this.updateCharts();
        }
      }),
      totalObs.subscribe((val: boolean) => {
        this.totalEnabled = val;
        const prev = this.showEfficiencyChart;
        this.showEfficiencyChart = this.adiabaticEnabled || this.totalEnabled;
        if (!prev && this.showEfficiencyChart) {
          setTimeout(() => this.updateCharts(), 0);
        } else {
          this.updateCharts();
        }
      }),
    );
  }

  private subscribeToHeatRecovery(): void {
    const heatObs = this.sharedStateService?.heatRecoveryUsed$ ?? of(false);
    const heatSub = heatObs.subscribe((val: boolean) => {
      this.heatRecoveryUsed = val;
      // Re-evaluate required fields when heat recovery usage changes
      if (this.latestFormState) {
        this.onFormValueChanged(this.latestFormState);
      }
    });
    this.subscriptions.push(heatSub);
  }

  charts = [
    { id: 'chart1', title: 'Vacuum vs Suction Volumetric Flow', yAxis: 'Vacuum [kPa]', marker: '48' },
    { id: 'chart2', title: 'Active Grid Power vs Suction Volumetric Flow', yAxis: 'Active Grid Power [kW]', marker: '382' },
    { id: 'chart3', title: 'Discharge Temperature vs Suction Volumetric Flow', yAxis: 'Discharge Temperature [°C]', marker: '120' },
    { id: 'chart4', title: 'Mass Flow vs Suction Volumetric Flow', yAxis: 'Mass flow [kg/s]', marker: '3.8' },
    {
      id: 'chart5',
      title: 'Discharge Volumetric Flow vs Suction Volumetric Flow',
      yAxis: 'Discharge Volumetric Flow [m³/s]',
      marker: '4.6',
    },
    { id: 'chart6', title: 'Efficiency vs Suction Volumetric Flow', yAxis: 'Efficiency [-]', marker: '4.6' },
  ];
  // Add this new property to hold dynamic markers
  markerYMap: Record<string, [number, number][]> = {};
  seriesMap: Record<string, any[]> = {};
  // Component.ts
  showSpeedLines: boolean = true; // default checked
  // show the efficiency chart (chart6) only when at least one efficiency is enabled
  showEfficiencyChart: boolean = true;
  private adiabaticEnabled: boolean = true;
  private totalEnabled: boolean = true;
  private subscriptions: Subscription[] = [];
  // Axis scaling values
  flowMin = 0;
  flowMax = 10;
  flowStep = 1;
  vacuumMin = 0;
  vacuumMax = 80;
  vacuumStep = 10;
  powerMin = 0;
  powerMax = 700;
  powerStep = 100;

  autoScaleFlow = true;
  autoScaleVacuum = true;
  autoScalePower = true;

  private resizeListener = () => {
    this.updateCharts();
  };

  ngAfterViewInit(): void {
    // Use setTimeout to ensure DOM is ready and charts init at correct size
    setTimeout(() => {
      this.updateCharts();
    });
    window.addEventListener('resize', this.resizeListener);
  }
  updateCharts(): void {
    this.charts.forEach((chart) => {
      const chartDom = document.getElementById(chart.id);
      if (!chartDom) {
        console.warn(`Chart element with id ${chart.id} not found`);
        return;
      }
      const myChart =
        echarts.getInstanceByDom(chartDom) || echarts.init(chartDom, null, { devicePixelRatio: window.devicePixelRatio || 2 });

      myChart.clear();
      myChart.setOption(this.buildChartOption(chart), true);
      myChart.resize();
    });
  }

  openChartFullscreen(chart: any): void {
    this.dialog.open(ChartFullscreenDialogComponent, {
      data: { title: chart.title, option: this.buildChartOption(chart) },
      width: '90vw',
      height: '85vh',
      maxWidth: '95vw',
      panelClass: 'chart-fullscreen-dialog-panel',
    });
  }

  private buildChartOption(chart: any): any {
    const baseSeries = this.buildBaseSeries(chart);

    this.addPreparedMarkers(chart, baseSeries);

    return {
      tooltip: {
        trigger: 'axis',
        textStyle: { fontSize: 10 },
        axisPointer: { type: 'none' },
        // Keep the tooltip within the chart bounds so overflow:hidden ancestors don't clip it.
        confine: true,
        formatter: (params: any) => {
          return params
            .filter((p: any) => p.seriesName) // exclude stalling lines with empty name
            .map((p: any) => {
              const xVal = typeof p.value[0] === 'number' ? p.value[0].toFixed(3) : p.value[0];
              const yVal = typeof p.value[1] === 'number' ? p.value[1].toFixed(3) : p.value[1];
              return `${p.seriesName}: [${xVal}, ${yVal}]`;
            })
            .join('<br/>');
        },
      },
      xAxis: this.getXAxis(),
      yAxis: this.getYAxisForChart(chart),
      series: baseSeries,
      legend: this.getLegendForChart(chart),
      grid: {
        top: 32,
        bottom: 60,
        left: 50,
        right: 60,
        containLabel: true,
      },
    };
  }

  // Extracted helpers to reduce cognitive complexity in updateCharts
  private buildBaseSeries(chart: any): any[] {
    const rawSeries = this.seriesMap[chart.id] || [];
    return rawSeries.map((s: any, index: number) => this.processSeriesEntry(s, index, chart)).filter(Boolean);
  }

  private processSeriesEntry(s: any, index: number, chart: any): any | null {
    // Early exit for speed lines on non-efficiency charts
    if (this.shouldHideSpeedLine(index, chart)) {
      return null;
    }

    const { name: computedName, lineType } = this.getNameAndLineType(chart.id, index, s.name);

    // Chart6 (efficiency) has special series-type handling
    if (chart.id === 'chart6') {
      const { seriesType, originalIndex } = this.determineChart6SeriesType(s, index);
      if (this.shouldHideChart6Series(seriesType, originalIndex)) {
        return null;
      }
      this.applyChart6SeriesMetadata(s, seriesType);
    } else {
      s.showInLegend = true;
    }

    const color = chart.id === 'chart6' ? s.lineStyle.color : this.commonColor;

    const sType = s.seriesType ?? null;
    const origIdx = s.originalIndex ?? index;
    this.addLabelsIfStalling(s, chart, sType, origIdx);

    return {
      ...s,
      name: computedName,
      lineStyle: { ...(s.lineStyle || {}), color, type: lineType },
      itemStyle: { ...(s.itemStyle || {}), color },
      showInLegend: s.showInLegend,
    };
  }

  private shouldHideSpeedLine(index: number, chart: any): boolean {
    return (index === 4 || index === 5) && !this.showSpeedLines && chart.id !== 'chart6';
  }

  private getNameAndLineType(chartId: string, index: number, originalName: string): { name: string; lineType: string } {
    let newName = originalName;
    let lineType = 'solid';
    if (chartId === 'chart1' || chartId === 'chart2') {
      if (index <= 3) {
        newName = 'Operations Area';
      } else if (index === 4 || index === 5) {
        newName = 'Middle Speeds';
        lineType = 'dashed';
      }
    } else if (chartId === 'chart3' || chartId === 'chart4' || chartId === 'chart5') {
      if (index <= 3) {
        newName = 'Operations Range';
      } else if (index === 4 || index === 5) {
        newName = 'Middle Speeds';
        lineType = 'dashed';
      }
    }
    return { name: newName, lineType };
  }

  private determineChart6SeriesType(s: any, index: number): { seriesType: string; originalIndex: number } {
    const greenSeries = [0, 1, 3, 4];
    const blueSeries = [5, 6, 8, 9];
    const stallingSeriesChart6 = [2, 7];

    let seriesType = s.seriesType ?? 'other';
    if (!s.seriesType) {
      if (greenSeries.includes(index)) {
        seriesType = 'adiabatic';
      } else if (blueSeries.includes(index)) {
        seriesType = 'total';
      } else if (stallingSeriesChart6.includes(index)) {
        seriesType = 'stalling';
      }
    }
    const originalIndex = s.originalIndex ?? index;
    return { seriesType, originalIndex };
  }

  private shouldHideChart6Series(seriesType: string, originalIndex: number): boolean {
    if ((originalIndex === 2 && !this.adiabaticEnabled) || (originalIndex === 7 && !this.totalEnabled)) {
      return true;
    }
    if ((seriesType === 'adiabatic' && !this.adiabaticEnabled) || (seriesType === 'total' && !this.totalEnabled)) {
      return true;
    }
    return false;
  }

  private applyChart6SeriesMetadata(s: any, seriesType: string): void {
    if (seriesType === 'adiabatic') {
      s.name = 'Adiabatic Efficiency';
    } else if (seriesType === 'total') {
      s.name = 'Total Efficiency';
    } else if (seriesType === 'stalling') {
      s.name = '';
    }

    if (seriesType === 'stalling') {
      s.showInLegend = false;
    } else if (seriesType === 'adiabatic') {
      s.showInLegend = !!this.adiabaticEnabled;
    } else if (seriesType === 'total') {
      s.showInLegend = !!this.totalEnabled;
    } else {
      s.showInLegend = true;
    }
  }

  private addLabelsIfStalling(s: any, chart: any, sType: any, origIdx: number): void {
    if (!(sType === 'stalling' || [2, 7].includes(origIdx))) {
      return;
    }

    const label = this.buildStallingLabel(chart.id, origIdx, s);
    if (label) {
      s.label = label;
    }
  }

  private buildStallingLabel(chartId: string, origIdx: number, s: any): any | undefined {
    // chart6, origIdx 7: bottom
    if (chartId === 'chart6' && origIdx === 7) {
      return {
        show: true,
        position: 'bottom',
        fontSize: 10,
        formatter: (params: any) => this.outerkeys?.[params.dataIndex] ?? '',
      };
    }

    // charts 4 & 5, origIdx 2: bottom, hide labels for intermediate points when speed lines hidden
    if ((chartId === 'chart4' || chartId === 'chart5') && origIdx === 2) {
      return {
        show: true,
        position: 'bottom',
        fontSize: 10,
        formatter: (params: any) => {
          if (!this.showSpeedLines && params.dataIndex > 0 && params.dataIndex < s.data.length - 1) {
            return '';
          }
          return this.outerkeys?.[params.dataIndex] ?? '';
        },
      };
    }

    // charts 1,2,3, origIdx 2: left, same hide behavior as above
    if ((chartId === 'chart1' || chartId === 'chart2' || chartId === 'chart3') && origIdx === 2) {
      return {
        show: true,
        position: 'left',
        fontSize: 10,
        formatter: (params: any) => {
          if (!this.showSpeedLines && params.dataIndex > 0 && params.dataIndex < s.data.length - 1) {
            return '';
          }
          return this.outerkeys?.[params.dataIndex] ?? '';
        },
      };
    }

    return undefined;
  }

  private addPreparedMarkers(chart: any, baseSeries: any[]): void {
    const markers = this.markerYMap[chart.id] || [];
    const preparedMarkers = this.filterMarkers(chart, markers);

    if (preparedMarkers.length > 0) {
      const seriesData = preparedMarkers.map(({ point: [x, y], origIdx }) => this.createMarkerDataPoint(x, y, origIdx, chart.id));

      baseSeries.push({
        name: 'Operating Point',
        type: 'scatter',
        symbol: 'circle',
        symbolSize: 10,
        data: seriesData,
      });
    }
  }

  private filterMarkers(chart: any, markers: any[]): Array<{ point: [number, number]; origIdx: number }> {
    const preparedMarkers: Array<{ point: [number, number]; origIdx: number }> = [];
    markers.forEach((pt, idx) => {
      if (this.shouldIncludeMarker(chart.id, idx)) {
        preparedMarkers.push({ point: pt, origIdx: idx });
      }
    });
    return preparedMarkers;
  }

  private shouldIncludeMarker(chartId: string, markerIdx: number): boolean {
    if (chartId !== 'chart6') {
      return true;
    }

    if (markerIdx === 0) {
      return this.adiabaticEnabled;
    }

    if (markerIdx === 1) {
      return this.totalEnabled;
    }

    return true;
  }

  private createMarkerDataPoint(x: number, y: number, origIdx: number, chartId: string): any {
    const markerColor = this.getMarkerColor(origIdx, chartId);

    return {
      value: [x, y],
      label: {
        show: true,
        formatter: () => y.toFixed(1),
        position: 'right',
        fontSize: 10,
        color: '#000',
      },
      itemStyle: { color: markerColor },
    };
  }

  private getMarkerColor(origIdx: number, chartId: string): string {
    const defaultColor = '#d44e06';

    if (chartId !== 'chart6') {
      return defaultColor;
    }

    if (origIdx === 0) {
      return 'green';
    }

    if (origIdx === 1) {
      return this.commonColor;
    }

    return defaultColor;
  }

  private getXAxis(): any {
    return {
      type: 'value',
      name: 'Suction Volumetric Flow [m³/s]',
      nameLocation: 'middle',
      min: this.autoScaleFlow ? null : this.flowMin,
      max: this.autoScaleFlow ? null : this.flowMax,
      interval: this.autoScaleFlow ? null : this.flowStep,
      nameTextStyle: { fontSize: 12, padding: [20, 0, 0, 0] },
    };
  }

  private getYAxisForChart(chart: any): any {
    if (chart.title.includes('Vacuum')) {
      return {
        type: 'value',
        name: chart.yAxis,
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 35,
        min: this.autoScaleVacuum ? null : this.vacuumMin,
        max: this.autoScaleVacuum ? null : this.vacuumMax,
        interval: this.autoScaleVacuum ? null : this.vacuumStep,
        nameTextStyle: { fontSize: 12, color: '#868890' },
      };
    } else if (chart.title.includes('Power')) {
      return {
        type: 'value',
        name: chart.yAxis,
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 35,
        min: this.autoScalePower ? null : this.powerMin,
        max: this.autoScalePower ? null : this.powerMax,
        interval: this.autoScalePower ? null : this.powerStep,
        nameTextStyle: { fontSize: 12, color: '#868890' },
      };
    }

    return {
      type: 'value',
      name: chart.yAxis,
      nameLocation: 'middle',
      nameRotate: 90,
      nameGap: 35,
      nameTextStyle: { fontSize: 12, color: '#868890' },
    };
  }

  private getLegendForChart(chart: any): any {
    if (chart.id === 'chart6') {
      const legendData: string[] = [];
      if (this.adiabaticEnabled) {
        legendData.push('Adiabatic Efficiency');
      }
      if (this.totalEnabled) {
        legendData.push('Total Efficiency');
      }
      return legendData.length ? { show: true, data: legendData, top: 27 } : { show: false };
    }
    return { show: false };
  }

  updateChartWithCachedData(model: any) {
    const operatingPoints = model.operatingPoints || [];
    // Extract outerkeys from backend response
    if (model.outerkeys) {
      this.outerkeys = model.outerkeys.map(Number);
    }

    this.buildMarkerYMap(operatingPoints);

    const chartDataMap = this.getChartDataMap(model);

    this.populateSeriesMap(chartDataMap);

    this.updateCharts();
  }

  private buildMarkerYMap(operatingPoints: any[]): void {
    this.markerYMap = {
      chart1: operatingPoints[0] ? [operatingPoints[0]] : [],
      chart2: operatingPoints[1] ? [operatingPoints[1]] : [],
      chart3: operatingPoints[3] ? [operatingPoints[3]] : [],
      chart4: operatingPoints[2] ? [operatingPoints[2]] : [],
      chart5: operatingPoints[4] ? [operatingPoints[4]] : [],
      chart6: [operatingPoints[5], operatingPoints[6]].filter(Boolean), // two points for chart 6
    };
  }

  private getChartDataMap(model: any): Record<string, any[]> {
    return {
      chart1: [
        model.minSpeedSuctionVolumetricFlowvsVacuum,
        model.maxSpeedSuctionVolumetricFlowvsVacuum,
        model.stallingSuctionVolumetricFlowvsVacuum,
        model.maxPowerSuctionVolumetricFlowvsVacuum,
        model.speedLine1SuctionVolumetricFlowvsVacuum,
        model.speedLine2SuctionVolumetricFlowvsVacuum,
      ],
      chart2: [
        model.minSpeedSuctionVolumetricFlowvsActiveGridPower,
        model.maxSpeedSuctionVolumetricFlowvsActiveGridPower,
        model.stallingSuctionVolumetricFlowvsActiveGridPower,
        model.maxPowerSuctionVolumetricFlowvsActiveGridPower,
        model.speedLine1SuctionVolumetricFlowvsActiveGridPower,
        model.speedLine2SuctionVolumetricFlowvsActiveGridPower,
      ],
      chart3: [
        model.minSpeedSuctionVolumetricFlowvsDischargeTemp,
        model.maxSpeedSuctionVolumetricFlowvsDischargeTemp,
        model.stallingSuctionVolumetricFlowvsDischargeTemp,
        model.maxPowerSuctionVolumetricFlowvsDischargeTemp,
        model.speedLine1SuctionVolumetricFlowvsDischargeTemp,
        model.speedLine2SuctionVolumetricFlowvsDischargeTemp,
      ],
      chart4: [
        model.minSpeedSuctionVolumetricFlowvsMassFlow,
        model.maxSpeedSuctionVolumetricFlowvsMassFlow,
        model.stallingSuctionVolumetricFlowvsMassFlow,
        model.maxPowerSuctionVolumetricFlowvsMassFlow,
        model.speedLine1SuctionVolumetricFlowvsMassFlow,
        model.speedLine2SuctionVolumetricFlowvsMassFlow,
      ],
      chart5: [
        model.minSpeedSuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.maxSpeedSuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.stallingSuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.maxPowerSuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.speedLine1SuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.speedLine2SuctionVolumetricFlowvsDischargeVolumetricFlow,
      ],
      chart6: [
        model.minSpeedSuctionVolumetricFlowvsAdiabaticEfficiency,
        model.maxSpeedSuctionVolumetricFlowvsAdiabaticEfficiency,
        model.stallingSuctionVolumetricFlowvsAdiabaticEfficiency,
        model.speedLine1SuctionVolumetricFlowvsAdiabaticEfficiency,
        model.speedLine2SuctionVolumetricFlowvsAdiabaticEfficiency,
        model.minSpeedSuctionVolumetricFlowvsTotalEfficiency,
        model.maxSpeedSuctionVolumetricFlowvsTotalEfficiency,
        model.stallingSuctionVolumetricFlowvsTotalEfficiency,
        model.speedLine1SuctionVolumetricFlowvsTotalEfficiency,
        model.speedLine2SuctionVolumetricFlowvsTotalEfficiency,
      ],
    };
  }

  private populateSeriesMap(chartDataMap: Record<string, any[]>): void {
    Object.keys(chartDataMap).forEach((chartId) => {
      if (chartId === 'chart6') {
        const stallingSeriesChart = [2, 7]; // red
        const adiabaticEfficiencySeries = [0, 1, 3, 4]; // green
        const totalEfficiencySeries = [5, 6, 8, 9]; // blue

        this.seriesMap[chartId] = chartDataMap[chartId].map((data, idx) => {
          let color = 'black';
          if (stallingSeriesChart.includes(idx)) {
            color = 'red';
          } else if (adiabaticEfficiencySeries.includes(idx)) {
            color = 'green';
          } else if (totalEfficiencySeries.includes(idx)) {
            color = this.commonColor;
          }

          return {
            name: `Series ${idx + 1}`,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            data,
            lineStyle: { color },
            itemStyle: { color },
            showInLegend: !stallingSeriesChart.includes(idx),
          };
        });
      } else {
        this.seriesMap[chartId] = chartDataMap[chartId].map((data) => ({
          name: `Series`,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          data,
          lineStyle: { color: this.commonColor },
          itemStyle: { color: this.commonColor },
          showInLegend: true,
        }));
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    window.removeEventListener('resize', this.resizeListener);
  }
}
